// 商户端多语言（i18n）初始化
//
// - 6 语言：zh / en / ja / ko / th / ms
// - fallbackLng: 'en'
// - 检测顺序：localStorage(fs_merchant_lang) → navigator → en
// - 注意：托管收银台（src/pages/checkout/*）有独立 i18n，不走此处。

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import zh from './locales/zh.json';
import en from './locales/en.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import th from './locales/th.json';
import ms from './locales/ms.json';

// localStorage 持久化 key
export const LANG_STORAGE_KEY = 'fs_merchant_lang';

// 支持的语言代码
export const SUPPORTED_LANGS = ['zh', 'en', 'ja', 'ko', 'th', 'ms'] as const;
export type AppLang = (typeof SUPPORTED_LANGS)[number];

// 顶部切换器选项（label 为各语言自身名称）
export const LANGUAGE_OPTIONS: { code: AppLang; label: string }[] = [
  { code: 'zh', label: '中文' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'th', label: 'ไทย' },
  { code: 'ms', label: 'Bahasa Melayu' },
];

const resources = {
  zh: { translation: zh },
  en: { translation: en },
  ja: { translation: ja },
  ko: { translation: ko },
  th: { translation: th },
  ms: { translation: ms },
};

// navigator 语言 → 支持的语言码
function mapNavigatorLang(): AppLang | null {
  if (typeof navigator === 'undefined') return null;
  const raw = (navigator.language || (navigator as any).userLanguage || '').toLowerCase();
  if (!raw) return null;
  if (raw.startsWith('zh')) return 'zh';
  if (raw.startsWith('ja')) return 'ja';
  if (raw.startsWith('ko')) return 'ko';
  if (raw.startsWith('th')) return 'th';
  if (raw.startsWith('ms')) return 'ms';
  if (raw.startsWith('en')) return 'en';
  return null;
}

// 自定义检测：localStorage → navigator → en
function detectInitialLang(): AppLang {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored && (SUPPORTED_LANGS as readonly string[]).includes(stored)) {
      return stored as AppLang;
    }
  } catch (e) {
    // localStorage 不可用时忽略
  }
  return mapNavigatorLang() || 'en';
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: detectInitialLang(),
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
  });

// 切换语言并持久化
export function changeAppLanguage(code: AppLang): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch (e) {
    // 忽略持久化失败
  }
  i18n.changeLanguage(code);
}

export default i18n;
